/**
 * InvoiceTemplates.tsx
 * Template management page — list templates, create/edit/delete with full line item editor.
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface TemplateLineItem {
  id: string;
  description: string;
  quantity: number;
  amount: number; // dollars
}

interface BillingTemplate {
  id: number;
  name: string;
  netTerms: number;
  footer: string;
  lineItems: Array<{ description: string; quantity: number; amount: number }>;
}

const NET_TERMS = [
  { value: 0, label: "Due on Receipt" },
  { value: 7, label: "Net 7" },
  { value: 15, label: "Net 15" },
  { value: 30, label: "Net 30" },
  { value: 45, label: "Net 45" },
  { value: 60, label: "Net 60" },
];

function uuid(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function netTermsLabel(days: number): string {
  const term = NET_TERMS.find((t) => t.value === days);
  return term ? term.label : `Net ${days}`;
}

function TemplateEditor({
  template,
  api,
  onSaved,
  onClose,
}: {
  template: BillingTemplate | null;
  api: ReturnType<typeof useApi>["admin"];
  onSaved: () => void;
  onClose: () => void;
}) {
  const toastAlert = useToastAlert();
  const isNew = template === null;

  const [name, setName] = useState(template?.name ?? "");
  const [netTerms, setNetTerms] = useState(template?.netTerms ?? 30);
  const [footer, setFooter] = useState(template?.footer ?? "");
  const [lineItems, setLineItems] = useState<TemplateLineItem[]>(
    (template?.lineItems?.length
      ? template.lineItems
      : [{ description: "", quantity: 1, amount: 0 }]
    ).map((li) => ({
      id: uuid(),
      description: li.description,
      quantity: li.quantity,
      amount: li.amount / 100,
    }))
  );
  const [saving, setSaving] = useState(false);

  const inputClass =
    "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500 bg-white";
  const labelClass = "block text-sm font-medium text-slate-700 mb-1";

  function updateItem(id: string, field: keyof TemplateLineItem, value: string | number) {
    setLineItems((prev) => prev.map((li) => (li.id === id ? { ...li, [field]: value } : li)));
  }

  function removeItem(id: string) {
    setLineItems((prev) => prev.filter((li) => li.id !== id));
  }

  function addItem() {
    setLineItems((prev) => [...prev, { id: uuid(), description: "", quantity: 1, amount: 0 }]);
  }

  async function handleSave() {
    if (!name.trim()) {
      toastAlert.displayToast(Severity.Warning, "Template name is required.");
      return;
    }
    setSaving(true);
    const body = {
      name: name.trim(),
      netTerms,
      footer,
      lineItems: lineItems
        .filter((li) => li.description)
        .map((li) => ({
          description: li.description,
          quantity: li.quantity,
          amount: Math.round(li.amount * 100),
        })),
    };
    try {
      const resp = isNew
        ? await api.post("api/BillingTemplate", body)
        : await api.put(`api/BillingTemplate/${template!.id}`, body);

      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, isNew ? "Template created." : "Template saved.");
        onSaved();
        onClose();
      } else {
        const err = await resp.json().catch(() => ({ message: "Save failed." }));
        toastAlert.displayToast(
          Severity.Error,
          (err as Record<string, string>).message || "Save failed."
        );
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save template.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <h3 className="text-base font-semibold text-slate-800">
            {isNew ? "New Template" : "Edit Template"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <label className={labelClass}>Template Name *</label>
            <input
              type="text"
              className={inputClass}
              placeholder="e.g., Monthly Support Plan"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label className={labelClass}>Net Terms</label>
            <select
              className={inputClass}
              value={netTerms}
              onChange={(e) => setNetTerms(parseInt(e.target.value, 10))}
            >
              {NET_TERMS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Footer Notes</label>
            <textarea
              className={inputClass}
              rows={2}
              placeholder="Payment instructions, notes, etc."
              value={footer}
              onChange={(e) => setFooter(e.target.value)}
            />
          </div>

          <div>
            <label className={labelClass}>Line Items</label>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-[1fr_70px_100px_28px] gap-2 bg-slate-50 px-3 py-2 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase">
                <span>Description</span>
                <span>Qty</span>
                <span>Price ($)</span>
                <span />
              </div>
              <div className="divide-y divide-slate-100">
                {lineItems.map((li) => (
                  <div
                    key={li.id}
                    className="grid grid-cols-[1fr_70px_100px_28px] gap-2 px-3 py-2 items-center"
                  >
                    <input
                      type="text"
                      className="text-sm border-0 outline-none bg-transparent w-full"
                      placeholder="Item description"
                      value={li.description}
                      onChange={(e) => updateItem(li.id, "description", e.target.value)}
                    />
                    <input
                      type="number"
                      className="text-sm border border-slate-200 rounded px-2 py-1 text-center w-full"
                      min="1"
                      value={li.quantity}
                      onChange={(e) =>
                        updateItem(li.id, "quantity", parseFloat(e.target.value) || 1)
                      }
                    />
                    <input
                      type="number"
                      className="text-sm border border-slate-200 rounded px-2 py-1 w-full"
                      min="0"
                      step="0.01"
                      value={li.amount}
                      onChange={(e) =>
                        updateItem(li.id, "amount", parseFloat(e.target.value) || 0)
                      }
                    />
                    <button
                      onClick={() => removeItem(li.id)}
                      className="text-slate-300 hover:text-red-400 text-sm"
                      aria-label="Remove line item"
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="px-3 py-2 border-t border-slate-100">
                <button
                  onClick={addItem}
                  className="text-xs text-cornflower-blue-600 hover:underline flex items-center gap-1"
                >
                  <i className="fa-solid fa-plus" /> Add line
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="shrink-0 flex gap-3 px-6 py-4 border-t border-slate-200 bg-white">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-cornflower-blue-600 text-white text-sm rounded-md hover:bg-cornflower-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <i className="fa-solid fa-spinner fa-spin" />}
            {saving ? "Saving…" : isNew ? "Create Template" : "Save Template"}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InvoiceTemplates() {
  const api = useApi();
  const toastAlert = useToastAlert();

  const [templates, setTemplates] = useState<BillingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  // undefined = editor closed, null = new template, BillingTemplate = editing existing
  const [editingTemplate, setEditingTemplate] = useState<BillingTemplate | null | undefined>(
    undefined
  );
  const [deleting, setDeleting] = useState<number | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.admin.get("api/BillingTemplate");
      if (resp.ok) {
        const data = await resp.json();
        setTemplates(Array.isArray(data) ? data : []);
      }
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  async function handleDelete(tpl: BillingTemplate) {
    if (!confirm(`Delete template "${tpl.name}"?`)) return;
    setDeleting(tpl.id);
    try {
      const resp = await api.admin.delete(`api/BillingTemplate/${tpl.id}`);
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Template deleted.");
        fetchTemplates();
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to delete template.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to delete template.");
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="border-b border-slate-200 px-6 pt-5 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Invoice Templates</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Save reusable line items, net terms, and footer notes.
            </p>
          </div>
          <button
            onClick={() => setEditingTemplate(null)}
            className="px-4 py-2 rounded-md bg-cornflower-blue-600 text-white text-sm font-medium hover:bg-cornflower-blue-700 flex items-center gap-2"
          >
            <i className="fa-solid fa-plus" />
            New Template
          </button>
        </div>
      </div>

      <div className="px-6 py-5">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="fa-solid fa-spinner fa-spin text-slate-400 text-xl" />
          </div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <i className="fa-solid fa-file-lines text-4xl mb-3" />
            <p className="text-sm">No templates yet.</p>
            <button
              onClick={() => setEditingTemplate(null)}
              className="mt-4 px-4 py-2 text-sm text-cornflower-blue-600 border border-cornflower-blue-200 rounded-md hover:bg-cornflower-blue-50"
            >
              Create your first template
            </button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="grid grid-cols-[1fr_150px_100px_160px] gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase">
              <span>Template Name</span>
              <span>Net Terms</span>
              <span>Line Items</span>
              <span className="text-right">Actions</span>
            </div>
            <div className="divide-y divide-slate-100">
              {templates.map((tpl) => (
                <div
                  key={tpl.id}
                  className="grid grid-cols-[1fr_150px_100px_160px] gap-3 px-4 py-3 items-center hover:bg-slate-50"
                >
                  <span className="text-sm font-medium text-slate-800 truncate">{tpl.name}</span>
                  <span className="text-sm text-slate-600">{netTermsLabel(tpl.netTerms)}</span>
                  <span className="text-sm text-slate-500">
                    {tpl.lineItems?.length ?? 0} item
                    {(tpl.lineItems?.length ?? 0) !== 1 ? "s" : ""}
                  </span>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => setEditingTemplate(tpl)}
                      className="px-3 py-1.5 text-xs rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(tpl)}
                      disabled={deleting === tpl.id}
                      className="p-1.5 rounded text-slate-400 hover:text-red-500 hover:bg-slate-100 disabled:opacity-50"
                      aria-label="Delete template"
                    >
                      {deleting === tpl.id ? (
                        <i className="fa-solid fa-spinner fa-spin text-xs" />
                      ) : (
                        <i className="fa-solid fa-trash text-xs" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editingTemplate !== undefined && (
        <TemplateEditor
          template={editingTemplate}
          api={api.admin}
          onSaved={fetchTemplates}
          onClose={() => setEditingTemplate(undefined)}
        />
      )}
    </div>
  );
}
